import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiUserPlus, FiRefreshCw, FiLogOut, FiArrowLeft, FiUsers } from "react-icons/fi";
import {
    getHostels,
    getAllocations,
    vacateStudent,
    waiveHostelMonth,
} from "../../services/hostelService";
import AllocateModal from "../../components/hostel/AllocateModal";
import ReallocateModal from "../../components/hostel/ReallocateModal";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

export default function AllocationList() {
    const [searchParams] = useSearchParams();
    const hostelIdParam = searchParams.get("hostel") || "";
    const navigate = useNavigate();

    const [hostels, setHostels] = useState([]);
    const [selectedHostel, setSelectedHostel] = useState(hostelIdParam);
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [reallocateTarget, setReallocateTarget] = useState(null);

    // Waive state
    const [waiveTarget, setWaiveTarget] = useState(null);
    const [waiveMonth, setWaiveMonth] = useState("August 2026");
    const [waivedAmount, setWaivedAmount] = useState("");
    const [waivedReason, setWaivedReason] = useState("");
    const [submittingWaive, setSubmittingWaive] = useState(false);


    useEffect(() => {
        getHostels()
            .then((res) => setHostels(res.data || []))
            .catch(() => toast.error("Failed to load hostels"));
    }, []);

    const loadAllocations = async () => {
        setLoading(true);
        try {
            const params = { status: "Active" };
            if (selectedHostel) params.hostel = selectedHostel;
            const res = await getAllocations(params);
            setAllocations(res.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to load allocations");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAllocations();
    }, [selectedHostel]); // eslint-disable-line

    const handleVacate = async (allocation) => {
        if (
            !window.confirm(
                `Vacate ${allocation.student.fullName} from Room ${allocation.room.roomNumber}?`,
            )
        )
            return;
        try {
            await vacateStudent(allocation._id);
            toast.success("Student vacated");
            loadAllocations();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to vacate student");
        }
    };

    const handleWaiveSubmit = async (e) => {
        e.preventDefault();
        if (!waivedReason.trim()) {
            toast.error("Please enter a reason / justification for the waiver");
            return;
        }

        try {
            setSubmittingWaive(true);
            const monthYear = waiveMonth.toLowerCase().replace(/\s+/g, "-");
            await waiveHostelMonth(waiveTarget._id, {
                monthYear,
                monthName: waiveMonth,
                waivedAmount: Number(waivedAmount || 0),
                waivedReason: waivedReason.trim(),
            });
            toast.success(`Fee for ${waiveMonth} marked as waived`);
            setWaiveTarget(null);
            setWaivedReason("");
            setWaivedAmount("");
            loadAllocations();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to waive hostel fee");
        } finally {
            setSubmittingWaive(false);
        }
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <FiUsers /> Hostel Allocations
                    </h1>
                    <p className="page-subtitle">{allocations.length} active allocation(s)</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/hostels")}>
                        <FiArrowLeft /> Back
                    </button>
                    <button className="btn btn-primary" onClick={() => setShowAllocateModal(true)}>
                        <FiUserPlus /> Allocate Student
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="filter-bar">
                    <label>Filter by hostel:</label>
                    <select
                        className="form-control"
                        style={{ width: 200 }}
                        value={selectedHostel}
                        onChange={(e) => setSelectedHostel(e.target.value)}
                    >
                        <option value="">All Hostels</option>
                        {hostels.map((h) => (
                            <option key={h._id} value={h._id}>
                                {h.name}
                            </option>
                        ))}
                    </select>
                </div>

                {loading ? (
                    <div className="loading-center">
                        <div className="spinner" />
                        Loading allocations...
                    </div>
                ) : allocations.length === 0 ? (
                    <div className="empty-state">
                        <p>No active allocations{selectedHostel ? " for this hostel" : ""}.</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th>Hostel</th>
                                    <th>Room</th>
                                    <th>Bed</th>
                                    <th>Academic Year</th>
                                    <th>Allocated On</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allocations.map((a) => (
                                    <tr key={a._id}>
                                        <td>
                                            {a.student?.fullName}
                                            {a.student?.rollNo ? ` (${a.student.rollNo})` : ""}
                                        </td>
                                        <td>{a.hostel?.name}</td>
                                        <td>{a.room?.roomNumber}</td>
                                        <td>{a.bedNumber}</td>
                                        <td>{a.academicYear}</td>
                                        <td>{new Date(a.allocatedDate).toLocaleDateString()}</td>
                                        <td>
                                            <div className="table-actions" style={{ justifyContent: "flex-end" }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-success btn-sm"
                                                    onClick={() => navigate(`/hostel-fee-receipts/new?student=${a.student?._id || a.student}`)}
                                                    title="Collect hostel fee/rent"
                                                >
                                                    💳 Collect Rent
                                                </button>
                                                <button
                                                    className="btn btn-outline btn-sm"
                                                    style={{ color: "#7c3aed", borderColor: "#c4b5fd" }}
                                                    onClick={() => setWaiveTarget(a)}
                                                    title="Waive a month's hostel rent"
                                                >
                                                    ⚖ Waive Fee
                                                </button>
                                                <button
                                                    className="btn btn-outline btn-sm"
                                                    onClick={() => setReallocateTarget(a)}
                                                >
                                                    <FiRefreshCw /> Reallocate
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleVacate(a)}
                                                >
                                                    <FiLogOut /> Vacate
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Waive Fee Modal */}
            {waiveTarget && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "480px" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800, color: "#7c3aed" }}>
                                ⚖ Waive Hostel Fee / Month
                            </h3>
                            <button onClick={() => setWaiveTarget(null)} className="btn-close" style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>

                        <form onSubmit={handleWaiveSubmit} style={{ padding: "1.25rem" }}>
                            <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "6px", marginBottom: "1rem", fontSize: "12.5px" }}>
                                <div>Student: <strong>{waiveTarget.student?.fullName}</strong></div>
                                <div>Hostel: <strong>{waiveTarget.hostel?.name}</strong> · Room <strong>{waiveTarget.room?.roomNumber}</strong></div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Month *</label>
                                    <select
                                        className="form-control"
                                        value={waiveMonth}
                                        onChange={(e) => setWaiveMonth(e.target.value)}
                                        required
                                    >
                                        <option value="January 2026">January 2026</option>
                                        <option value="February 2026">February 2026</option>
                                        <option value="March 2026">March 2026</option>
                                        <option value="April 2026">April 2026</option>
                                        <option value="May 2026">May 2026</option>
                                        <option value="June 2026">June 2026</option>
                                        <option value="July 2026">July 2026</option>
                                        <option value="August 2026">August 2026</option>
                                        <option value="September 2026">September 2026</option>
                                        <option value="October 2026">October 2026</option>
                                        <option value="November 2026">November 2026</option>
                                        <option value="December 2026">December 2026</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Waived Amount (₹)</label>
                                    <input
                                        type="number"
                                        placeholder="Full rent or custom"
                                        className="form-control"
                                        value={waivedAmount}
                                        onChange={(e) => setWaivedAmount(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                    Justification / Reason for Waiver *
                                </label>
                                <textarea
                                    rows="3"
                                    placeholder="e.g. Admitted late on 24-Aug, or vacation / medical waiver approved by Principal..."
                                    className="form-control"
                                    value={waivedReason}
                                    onChange={(e) => setWaivedReason(e.target.value)}
                                    required
                                />
                            </div>

                            {/* Active Waivers list if any */}
                            {waiveTarget.waivers && waiveTarget.waivers.length > 0 && (
                                <div style={{ marginBottom: "1rem", background: "#faf5ff", padding: "8px 12px", borderRadius: "6px", border: "1px solid #e9d5ff" }}>
                                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#7c3aed", marginBottom: "4px" }}>
                                        Existing Waivers on this Stay ({waiveTarget.waivers.length}):
                                    </div>
                                    {waiveTarget.waivers.map((w, i) => (
                                        <div key={i} style={{ fontSize: "11.5px", display: "flex", justifyContent: "space-between", color: "#6b21a8" }}>
                                            <span>• {w.monthName}: "{w.waivedReason}"</span>
                                            {w.waivedAmount > 0 && <strong>{inr(w.waivedAmount)}</strong>}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                                <button type="button" onClick={() => setWaiveTarget(null)} className="btn btn-outline">Cancel</button>
                                <button type="submit" disabled={submittingWaive} className="btn btn-primary" style={{ background: "#7c3aed", borderColor: "#7c3aed" }}>
                                    {submittingWaive ? "Saving..." : "Confirm Waiver"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showAllocateModal && (
                <AllocateModal
                    defaultHostelId={selectedHostel}
                    onClose={() => setShowAllocateModal(false)}
                    onSuccess={() => {
                        setShowAllocateModal(false);
                        loadAllocations();
                    }}
                />
            )}

            {reallocateTarget && (
                <ReallocateModal
                    allocation={reallocateTarget}
                    onClose={() => setReallocateTarget(null)}
                    onSuccess={() => {
                        setReallocateTarget(null);
                        loadAllocations();
                    }}
                />
            )}
        </div>
    );
}